// components/FormComponents/CustomerForm.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete"; // 🔹 NEW
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as yup from "yup";
import { submitFormData } from "../../services/api.service";
import { Service, State } from "./CustomerForm.types";
import moment from "moment-timezone";
import "moment/locale/he";
import { pushGTMEvent } from "../../helper/GTMEventHelper";
import { PlanCardsSelect, type PlanItem } from "./PlanCardsSelect";
import { trackEvent } from "../../services/analytics.service";
import { useNavigate } from "react-router-dom";
import { ELECTRICITY_PLAN_THANK_YOU_PATH } from "../../pages/ElectricityPlanThankYou";

/**
 * NOTE:
 * Ensure your `State` in ./CustomerForm.types has these fields:
 *   cust_city: string;
 *   cust_address: string;     // street (renamed from cust_street)
 *   cust_house_num: string;
 *   cust_apt_num: string;
 *
 * And for changePlan_Pwr checkbox validation add:
 *   change_plan_terms_approved: boolean;
 */

const CHANGE_PLAN_SERVICE_VALUE = "changePlan_Pwr";
const MAMI_CHANGE_PLAN_SERVICE_VALUE = "mamiChangePlan_Pwr";
const REQUEST_DETAILS_SERVICE_VALUES = new Set([
  "checkAboutMigrationProcess_Pwr",
]);

const initialState: State = {
  contact_type: "",
  cust_first_name: "",
  cust_last_name: "",

  // 4-part address
  cust_city: "",
  cust_address: "", // street
  cust_house_num: "",
  cust_apt_num: "",

  cust_email: "",
  cust_phone: "",
  cust_phone_prefix: "",
  message: "",
  tech_type: "",
  tech_datetime: "",
  invoice_type: "",
  send_type: "",
  delivery_address: "",
  new_plan: "",
  new_email: "",

  change_plan_terms_approved: false,
  change_plan_cta_clicked_at: "",

  // LPG termination/transfer common fields
  lpg_consumer_number: "",
  lpg_meter_number: "",
  lpg_meter_reading: "",
  lpg_meter_photo: null,

  // Incoming customer (ownership transfer)
  incoming_first_name: "",
  incoming_last_name: "",
  incoming_phone_prefix: "", // 🔹 NEW
  incoming_phone: "",
  incoming_email: "",
};

const allowedPrefixes = [
  "050",
  "051",
  "052",
  "053",
  "054",
  "055",
  "056",
  "057",
  "058",
  "059",
];

const changePlanItems: PlanItem[] = [
  {
    value: "summer",
    title: "שעון קיץ",
    discountText: "21%",
    descriptionLines: [
      `20:00 בערב ועד`,
      `2:00 לפנות בוקר למחרת`,
      `בימים א-ה`,
      `(ללא סופ"ש)`,
    ],
    ctaText: "לתנאי המסלול",
    ctaDocPdf:
      "https://super-buy.co.il/wp-content/uploads/2026/05/%D7%94%D7%A1%D7%9B%D7%9D-%D7%9E%D7%9B%D7%99%D7%A8%D7%AA-%D7%97%D7%A9%D7%9E%D7%9C-%D7%91%D7%99%D7%AA%D7%99-%D7%A1%D7%95%D7%A4%D7%A8-%D7%A4%D7%90%D7%95%D7%95%D7%A8_%D7%9E%D7%A1%D7%9C%D7%95%D7%9C-%D7%A9%D7%A2%D7%95%D7%9F-%D7%A7%D7%99%D7%A5.pdf",
  },
  {
    value: "night",
    title: "לילה פלוס",
    discountText: "21%",
    descriptionLines: [
      `23:00 בלילה ועד`,
      `7:00 בבוקר למחרת`,
      `בימים א-ה`,
      `(ללא סופ"ש)`,
    ],
    ctaText: "לתנאי המסלול",
    ctaDocPdf:
      "https://epstorageprod01.blob.core.windows.net/selfservice/הסכם מכירת חשמל ביתי סופר פאוור_מסלול לילה.pdf",
  },
  {
    value: "power",
    title: "POWER",
    discountText: "6.5%",
    descriptionLines: [`הנחה קבועה`, `(24/7)`, `בכל ימות השבוע`],
    ctaText: "לתנאי המסלול",
    ctaDocPdf:
      "https://epstorageprod01.blob.core.windows.net/selfservice/הסכם מכירת חשמל ביתי סופר פאוור_פאוור גולד.pdf",
  },
  {
    value: "hightech",
    title: "מסלול יום",
    discountText: "21%",
    descriptionLines: [
      `7:00 בבוקר עד`,
      `17:00 אחה"צ`,
      `בימים א-ה`,
      `(ללא סופ"ש)`,
    ],
    ctaText: "לתנאי המסלול",
    ctaDocPdf: `https://super-buy.co.il/wp-content/uploads/2026/05/%D7%94%D7%A1%D7%9B%D7%9D-%D7%9E%D7%9B%D7%99%D7%A8%D7%AA-%D7%97%D7%A9%D7%9E%D7%9C-%D7%91%D7%99%D7%AA%D7%99-%D7%A1%D7%95%D7%A4%D7%A8-%D7%A4%D7%90%D7%95%D7%95%D7%A8_%D7%9E%D7%A1%D7%9C%D7%95%D7%9C-%D7%99%D7%95%D7%9D-%D7%A4%D7%9C%D7%95%D7%A1.pdf`,
  },
];

function getChangePlanTitle(planValue: string) {
  return changePlanItems.find((item) => item.value === planValue)?.title ?? null;
}

function getNextAvailableDateTimeOptions(): { value: string; label: string }[] {
  const tz = "Asia/Jerusalem";
  const options: { value: string; label: string }[] = [];
  let date = moment().tz(tz).add(2, "days").startOf("day");
  const windows = [
    { start: 8, end: 10 },
    { start: 10, end: 12 },
    { start: 12, end: 14 },
    { start: 14, end: 16 },
  ];
  let daysCollected = 0;
  while (daysCollected < 3) {
    if (date.day() !== 5 && date.day() !== 6) {
      windows.forEach(({ start, end }) => {
        const slotStart = date.clone().hour(start).minute(0);
        const slotEnd = date.clone().hour(end).minute(0);
        options.push({
          value: `${slotStart.format("DD/MM/YYYY HH:mm")}-${slotEnd.format("HH:mm")}`,
          label:
            slotStart.locale("he").format("dddd, DD/MM/YYYY ") +
            `${slotStart.format("HH:mm")}-${slotEnd.format("HH:mm")}`,
        });
      });
      daysCollected++;
    }
    date.add(1, "day");
  }
  return options;
}

const hebrewOnly = /^[\u0590-\u05FF\s'-]+$/;
const hebrewOnlyWithMore = /^[\u0590-\u05FF0-9\s\-;:'",.]+$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validationSchema = yup.object().shape({
  cust_first_name: yup
    .string()
    .matches(hebrewOnly, "יש להזין בעברית בלבד")
    .required("יש להזין שם פרטי"),
  cust_last_name: yup
    .string()
    .matches(hebrewOnly, "יש להזין בעברית בלבד")
    .required("יש להזין שם משפחה"),

  // 4-part address (all mandatory, Hebrew-only)
  cust_city: yup
    .string()
    //.matches(hebrewOnly, "יש להזין בעברית בלבד")
    .required("שדה חובה"),
  cust_address: yup
    .string()
    // .matches(hebrewOnly, "יש להזין בעברית בלבד")
    .required("שדה חובה"),
  cust_house_num: yup.string().required("שדה חובה"),
  cust_apt_num: yup.string().required("שדה חובה"),

  cust_email: yup
    .string()
    .transform((v) => (v === undefined || v === null ? "" : v))
    .test(
      "is-valid-or-empty",
      "כתובת דואר אלקטרוני אינה תקינה: יש להזין כתובת באנגלית בלבד ולהכיל את הסימן @",
      (value) => !value || emailRegex.test(value),
    )
    .notRequired(),

  cust_phone_prefix: yup.string().required("יש להזין קידומת"),
  cust_phone: yup
    .string()
    .required("יש להזין טלפון")
    .min(7, "מספר לא תקין - קצר מידי")
    .matches(/^\d+$/, "יש להזין ספרות בלבד"),

  message: yup.string().max(1000, "הודעה לא יכולה להיות מעל 1000 תווים"),

  tech_datetime: yup.string().when("$service", {
    is: (val: string) => val === "tecCheckSchedule_Lpg",
    then: (s) => s.required("יש לבחור תאריך ושעה"),
    otherwise: (s) => s.notRequired(),
  }),

  invoice_type: yup.string().when("$service", {
    is: (val: string) =>
      val === "invOrRecRequest_Lpg" || val === "invoiceCopyRequest_Pwr",
    then: (s) => s.required("יש לבחור סוג מסמך"),
    otherwise: (s) => s.notRequired(),
  }),

  send_type: yup.string().when("$service", {
    is: (val: string) => val === "invOrRecRequest_Lpg",
    then: (s) => s.required("יש לבחור אופן משלוח"),
    otherwise: (s) => s.notRequired(),
  }),

  delivery_address: yup.string().when(["$service", "send_type"], {
    is: (service: string, send_type: string) =>
      service === "invOrRecRequest_Lpg" && send_type === "address",
    then: (s) =>
      s
        .required("יש להזין כתובת למשלוח")
        .matches(hebrewOnlyWithMore, "יש להזין כתובת בעברית בלבד"),
    otherwise: (s) => s.notRequired(),
  }),

  new_plan: yup.string().when("$service", {
    is: (val: string) => val === CHANGE_PLAN_SERVICE_VALUE,
    then: (s) => s.required("יש לבחור מסלול חדש"),
    otherwise: (s) => s.notRequired(),
  }),

  // ✅ NEW: checkbox must be checked for changePlan_Pwr
  change_plan_terms_approved: yup.boolean().when("$service", {
    is: (val: string) => val === CHANGE_PLAN_SERVICE_VALUE,
    then: (s) =>
      s.oneOf([true], "יש לאשר שקראת את תנאי המסלול לפני שליחה").required(),
    otherwise: (s) => s.notRequired(),
  }),

  new_email: yup.string().when("$service", {
    is: (val: string) => val === "updateEmail_Pwr",
    then: (s) =>
      s
        .required("יש להזין כתובת דואר אלקטרוני חדשה")
        .matches(
          emailRegex,
          "כתובת דואר אלקטרוני אינה תקינה: יש להזין כתובת באנגלית בלבד ולהכיל את הסימן @",
        ),
    otherwise: (s) => s.notRequired(),
  }),

  lpg_consumer_number: yup
    .string()
    .matches(/^\d*$/, "יש להזין ספרות בלבד")
    .notRequired(),
  lpg_meter_number: yup.string().when("$service", {
    is: (val: string) =>
      val === "terminationOfService_Lpg" || val === "ownershipTransfer_Lpg",
    then: (s) =>
      s.required("יש להזין מספר מונה").matches(/^\d+$/, "יש להזין ספרות בלבד"),
    otherwise: (s) => s.notRequired(),
  }),
  lpg_meter_reading: yup.string().when("$service", {
    is: (val: string) =>
      val === "terminationOfService_Lpg" || val === "ownershipTransfer_Lpg",
    then: (s) =>
      s
        .required("יש להזין קריאת מונה עדכנית")
        .matches(/^\d+$/, "יש להזין ספרות בלבד"),
    otherwise: (s) => s.notRequired(),
  }),
  lpg_meter_photo: yup.mixed<File>().when("$service", {
    is: (val: string) =>
      val === "terminationOfService_Lpg" || val === "ownershipTransfer_Lpg",
    then: (s) =>
      s
        .test("is-file", "יש להעלות צילום מונה", (v) => v instanceof File)
        .required("יש להעלות צילום מונה"),
    otherwise: (s) => s.notRequired(),
  }),

  incoming_first_name: yup.string().when("$service", {
    is: (val: string) => val === "ownershipTransfer_Lpg",
    then: (s) =>
      s
        .required("יש להזין שם פרטי")
        .matches(hebrewOnly, "יש להזין בעברית בלבד"),
    otherwise: (s) => s.notRequired(),
  }),
  incoming_last_name: yup.string().when("$service", {
    is: (val: string) => val === "ownershipTransfer_Lpg",
    then: (s) =>
      s
        .required("יש להזין שם משפחה")
        .matches(hebrewOnly, "יש להזין בעברית בלבד"),
    otherwise: (s) => s.notRequired(),
  }),

  // 🔹 NEW: prefix required when ownershipTransfer_Lpg
  incoming_phone_prefix: yup.string().when("$service", {
    is: (val: string) => val === "ownershipTransfer_Lpg",
    then: (s) => s.required("יש להזין קידומת"),
    otherwise: (s) => s.notRequired(),
  }),

  incoming_phone: yup.string().when("$service", {
    is: (val: string) => val === "ownershipTransfer_Lpg",
    then: (s) =>
      s
        .required("יש להזין טלפון")
        .min(7, "מספר לא תקין - קצר מידי")
        .matches(/^\d+$/, "יש להזין ספרות בלבד"),
    otherwise: (s) => s.notRequired(),
  }),
  incoming_email: yup
    .string()
    .transform((v) => (v === undefined || v === null ? "" : v))
    .test(
      "is-valid-or-empty-incoming",
      "כתובת דואר אלקטרוני אינה תקינה: יש להזין כתובת באנגלית בלבד ולהכיל את הסימן @",
      (value) => !value || emailRegex.test(value),
    )
    .notRequired(),
});

function getTitleByServiceValue(value: string) {
  switch (value) {
    case "checkAboutMigrationProcess_Pwr":
      return "לבירור תהליך הניוד יש למלא את הפרטים הבאים:";
    case CHANGE_PLAN_SERVICE_VALUE:
      return "לשינוי מסלול חשמל יש למלא את הפרטים הבאים:";
    case MAMI_CHANGE_PLAN_SERVICE_VALUE:
      return "למעבר למסלול מאמי למשפחה יש למלא את הפרטים הבאים:";
    case "creditCardUpdateRequest_Pwr":
      return "לטובת עדכון כרטיס אשראי יש למלא את הפרטים הבאים ואנו ניצור עמך קשר בהקדם";
    case "invoiceCopyRequest_Pwr":
      return "להעתק חשבונית יש למלא את הפרטים הבאים:";
    case "updateEmail_Pwr":
      return "לטובת עדכון כתובת דואר אלקטרוני יש למלא את הפרטים הבאים";
    case "tecCheckSchedule_Lpg":
      return "לתיאום טכנאי יש למלא את הפרטים הבאים:";
    case "invOrRecRequest_Lpg":
      return "להעתק חשבונית או קבלה יש למלא את הפרטים הבאים:";
    case "terminationOfService_Lpg":
      return "לביצוע סיום התקשרות לחשבון הגז – מלאו את הפרטים הבאים:   ";
    case "ownershipTransfer_Lpg":
      return "לעדכון העברת בעלות לחשבון הגז- מלאו את הפרטים הבאים:\n";
    default:
      return "";
  }
}

function getSuccessByService(value: string) {
  switch (value) {
    case "checkAboutMigrationProcess_Pwr":
      return {
        title: "בקשתך לבירור תהליך ניוד התקבלה.",
        subtitle:
          "אנו נשלח את סטטוס הניוד שלך לכתובת דואר האלקטרוני שציינת, מבטיחים לעדכן תוך 1 יום עסקים 🙂",
      };
    case CHANGE_PLAN_SERVICE_VALUE:
      return {
        title: "בקשתך לשינוי מסלול חשמל התקבלה.",
        subtitle:
          "אנו נשלח עדכון ברגע שהפעולה תתבצע, במידה וידרשו לנו פרטים נוספים, אנו ניצור איתך קשר 🙂",
      };
    case MAMI_CHANGE_PLAN_SERVICE_VALUE:
      return {
        title: "בקשתך למעבר למסלול מאמי למשפחה התקבלה.",
        subtitle:
          "אנו נשלח עדכון ברגע שהפעולה תתבצע, במידה וידרשו לנו פרטים נוספים, אנו ניצור איתך קשר 🙂",
      };
    case "creditCardUpdateRequest_Pwr":
      return {
        title: "בקשתך התקבלה. אנו ניצור איתך קשר תוך 1 יום עסקים 🙂",
        subtitle: "",
      };
    case "updateEmail_Pwr":
      return {
        title: "בקשתך לשינוי דואר אלקטרוני התקבלה.",
        subtitle:
          "אנו נשלח עדכון ברגע שהפעולה תתבצע, במידה וידרשו לנו פרטים נוספים, אנו ניצור קשר 🙂",
      };
    case "tecCheckSchedule_Lpg":
      return {
        title: "בקשתך לתיאום טכנאי התקבלה.",
        subtitle:
          "במידה ולא נוכל במועד שביקשת, אנו ניצור קשר לתיאום מועד מתאים.",
      };
    case "invOrRecRequest_Lpg":
    case "invoiceCopyRequest_Pwr":
      return {
        title: "קיבלנו את הבקשה מבטיחים לטפל בהקדם ועד 7 ימי עסקים.",
        subtitle: "במידה וידרשו לנו פרטים נוספים, ניצור קשר 🙂",
      };
    case "terminationOfService_Lpg":
      return {
        title: "תודה!",
        subtitle:
          "קיבלנו את הבקשה שלכם לביצוע סיום התקשרות לחשבון הגז.\nבימים הקרובים ועד 3 ימי עסקים, ניצור קשר טלפוני לביצוע גמר החשבון. \nלשם כך נבקש רק להכין את פרטי כרטיס האשראי\n",
      };
    case "ownershipTransfer_Lpg":
      return {
        title: "ברכות 🙂 איזו התחלה חדשה ומרגשת!",
        subtitle:
          "הבקשה שלכם התקבלה בהצלחה ואנו מטפלים בה. \n" +
          "בימים הקרובים (עד 3 ימי עסקים) ניצור קשר טלפוני להשלמת התהליך. \n" +
          "מול הלקוח היוצא – גמר חשבון ותשלום \n" +
          "מול הלקוח הנכנס – חתימה על הסכם הצטרפות ותשלום דמי העברת בעלות חד פעמי בסך 29.90 ₪ \n",
      };
    default:
      return { title: "הטופס נשלח בהצלחה!", subtitle: "" };
  }
}

function getAdditionalMessage(
  selectedServiceValue: string,
  fields: State,
): string {
  switch (selectedServiceValue) {
    case "tecCheckSchedule_Lpg":
      return `   תיאום טכנאי לבדיקת בטיחות -  
   תאריך ושעה:  ${fields.tech_datetime || ""}`;
    case "invOrRecRequest_Lpg": {
      const invoiceTypeLabelMap: Record<string, string> = {
        last_invoice: "חשבונית אחרונה",
        two_last_invoice: "שתי חשבוניות אחרונות",
        last_payment_receipt: "קבלה אחרונה",
        two_last_payment_receipt: "שתי קבלות אחרונות",
      };
      const sendTypeLabelMap: Record<string, string> = {
        address: "כתובת למשלוח",
        email: "דואר אלקטרוני",
      };
      return ` סוג מסמך: ${
        fields.invoice_type && invoiceTypeLabelMap[fields.invoice_type]
          ? invoiceTypeLabelMap[fields.invoice_type]
          : ""
      } אופן משלוח: ${
        fields.send_type && sendTypeLabelMap[fields.send_type]
          ? sendTypeLabelMap[fields.send_type]
          : ""
      }${
        fields.send_type === "address" && fields.delivery_address
          ? ` כתובת: ${fields.delivery_address}`
          : ""
      }`;
    }
    case "invoiceCopyRequest_Pwr": {
      const invoiceTypeLabelMap: Record<string, string> = {
        last_invoice: "חשבונית אחרונה",
        two_last_invoice: "שתי חשבוניות אחרונות",
      };
      return ` סוג חשבונית: ${
        fields.invoice_type && invoiceTypeLabelMap[fields.invoice_type]
          ? invoiceTypeLabelMap[fields.invoice_type]
          : ""
      } אופן משלוח: דואר אלקטרוני`;
    }
    case CHANGE_PLAN_SERVICE_VALUE: {
      const planLabelMap: Record<string, string> = {
        summer: "שעון קיץ - 21% בימים א עד ה בין השעות 20:00 עד 02:00",
        power: "פאוור – 6.5% הנחה בכל שעות היום",
        hightech: "יום – 21% בימים א עד ה בין השעות 07:00 עד 17:00",
        night: "לילה פלוס - 21% בימים א עד ה בין השעות 23:00 עד 07:00",
      };

      const planText =
        fields.new_plan && planLabelMap[fields.new_plan]
          ? planLabelMap[fields.new_plan]
          : "";

      const tz = "Asia/Jerusalem";
      const ctaPart = fields.change_plan_cta_clicked_at
        ? ` | צפייה בתנאי המסלול: ${moment(fields.change_plan_cta_clicked_at)
            .tz(tz)
            .format("DD/MM/YYYY HH:mm")}`
        : "";

      return `מסלול חדש: ${planText}${ctaPart} | קראתי ואישרתי את תנאי המסלול`;
    }
    case MAMI_CHANGE_PLAN_SERVICE_VALUE:
      return "מעבר למסלול מאמי למשפחה";
    case "updateEmail_Pwr":
      return ` דואר אלקטרוני חדש:  ${fields.new_email || ""}`;
    case "checkAboutMigrationProcess_Pwr":
      return `בירור תהליך ניוד חשמל ביתי`;
    case "creditCardUpdateRequest_Pwr":
      return " עדכון כרטיס אשראי - חשמל ביתי ";
    case "terminationOfService_Lpg":
      return [
        fields.lpg_consumer_number
          ? `מספר צרכן: ${fields.lpg_consumer_number}`
          : "",
        fields.lpg_meter_number ? `מספר מונה: ${fields.lpg_meter_number}` : "",
        fields.lpg_meter_reading
          ? `קריאת מונה עדכנית: ${fields.lpg_meter_reading}`
          : "",
        fields.message ? `הערות: ${fields.message}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
    case "ownershipTransfer_Lpg":
      return [
        fields.lpg_consumer_number
          ? `מספר צרכן: ${fields.lpg_consumer_number}`
          : "",
        fields.lpg_meter_number ? `מספר מונה: ${fields.lpg_meter_number}` : "",
        fields.lpg_meter_reading
          ? `קריאת מונה עדכנית: ${fields.lpg_meter_reading}`
          : "",
        fields.incoming_first_name
          ? `נכנס - שם פרטי: ${fields.incoming_first_name}`
          : "",
        fields.incoming_last_name
          ? `נכנס - שם משפחה: ${fields.incoming_last_name}`
          : "",
        fields.incoming_phone || fields.incoming_phone_prefix
          ? `נכנס - טלפון: ${(fields.incoming_phone_prefix || "") + (fields.incoming_phone || "")}`
          : "",
        fields.incoming_email ? `נכנס - דוא"ל: ${fields.incoming_email}` : "",
        fields.message ? `הערות: ${fields.message}` : "",
      ]
        .filter(Boolean)
        .join(" | ");
    default:
      return "";
  }
}

function stripChangePlanFields(fields: State) {
  const {
    new_plan: _newPlan,
    change_plan_terms_approved: _changePlanTermsApproved,
    change_plan_cta_clicked_at: _changePlanCtaClickedAt,
    ...rest
  } = fields;
  return rest;
}

type CustomerFormProps = {
  selectedService?: Service;
  showModal: ({
    type,
    title,
    subtitle,
    specialInfo,
  }: {
    type: "success" | "error" | "info";
    title: string;
    subtitle?: string;
    specialInfo?: string;
  }) => void;
  onSuccess?: () => void;
  hideChangePlanPowerItem?: boolean;
};

export default function CustomerForm({
  selectedService,
  showModal,
  onSuccess,
  hideChangePlanPowerItem = false,
}: CustomerFormProps) {
  const navigate = useNavigate();
  const [fields, setFields] = useState(initialState);
  const [errors, setErrors] = useState<{ [K in keyof State]?: string }>({});
  const [loading, setLoading] = useState(false);
  const visibleChangePlanItems = useMemo(
    () =>
      hideChangePlanPowerItem
        ? changePlanItems.filter((item) => item.value !== "power")
        : changePlanItems,
    [hideChangePlanPowerItem],
  );

  // -- Duplicate dialog logic --
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  // 🔹 Cities / streets data like OnBoardingPage.tsx
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<any | null>(null);

  // Load cities.json (place it under /public/cities.json)
  useEffect(() => {
    fetch("cities.json")
      .then((res) => res.json())
      .then((data: any[]) => setCities(data || []))
      .catch((e) => {
        console.error("Failed to load cities.json", e);
        setCities([]);
      });
  }, []);

  useEffect(() => {
    if (hideChangePlanPowerItem && fields.new_plan === "power") {
      setFields((s) => ({ ...s, new_plan: "" }));
    }
  }, [fields.new_plan, hideChangePlanPowerItem]);

  // Derive streets list from selected city
  const streetOptions: string[] = useMemo(() => {
    return selectedCity?.streets?.map((s: any) => s["שם_רחוב"]) ?? [];
  }, [selectedCity]);

  // Whenever city text changes, find city object and clear street
  useEffect(() => {
    if (!fields.cust_city) {
      setSelectedCity(null);
      return;
    }
    const found = cities.find((c) => c["שם_ישוב"] === fields.cust_city);
    setSelectedCity(found ?? null);

    // clear street when city changes (keep house/apt intact)
    setFields((s) => ({ ...s, cust_address: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.cust_city, cities]);

  // helper for numeric-only onChange
  const toDigits = (v: string) => v.replace(/\D+/g, "");

  function renderDynamicFields() {
    switch (selectedService?.value) {
      case "tecCheckSchedule_Lpg":
        return (
          <>
            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth error={!!errors.tech_datetime}>
                <InputLabel id="tech-datetime-label">תאריך ושעה</InputLabel>
                <Select
                  labelId="tech-datetime-label"
                  label="תאריך ושעה"
                  value={fields.tech_datetime || ""}
                  onChange={(e) =>
                    setFields((s) => ({ ...s, tech_datetime: e.target.value }))
                  }
                >
                  {getNextAvailableDateTimeOptions().map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <div style={{ color: "#d32f2f" }}>{errors.tech_datetime}</div>
              </FormControl>
            </div>
          </>
        );

      case "invOrRecRequest_Lpg":
        return (
          <>
            {/* חשבונית או קבלה + אופן משלוח */}
            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth>
                <InputLabel id="invoice-type-label">
                  חשבונית או קבלה למשלוח
                </InputLabel>
                <Select
                  labelId="invoice-type-label"
                  value={fields.invoice_type || ""}
                  label="חשבונית או קבלה"
                  onChange={(e) =>
                    setFields((s) => ({ ...s, invoice_type: e.target.value }))
                  }
                  sx={{ direction: "rtl", textAlign: "right" }}
                  inputProps={{ style: { textAlign: "right" } }}
                  error={!!errors.invoice_type}
                >
                  <MenuItem
                    value="last_invoice"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    חשבונית אחרונה
                  </MenuItem>
                  <MenuItem
                    value="two_last_invoice"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    שתי חשבוניות אחרונות
                  </MenuItem>
                  <MenuItem
                    value="last_payment_receipt"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    קבלה אחרונה
                  </MenuItem>
                  <MenuItem
                    value="two_last_payment_receipt"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    שתי קבלות אחרונות
                  </MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth>
                <InputLabel id="send-type-label">אופן משלוח</InputLabel>
                <Select
                  labelId="send-type-label"
                  value={fields.send_type || ""}
                  label="אופן משלוח *"
                  onChange={(e) =>
                    setFields((s) => ({ ...s, send_type: e.target.value }))
                  }
                  error={!!errors.send_type}
                >
                  <MenuItem value="address">כתובת למשלוח</MenuItem>
                  <MenuItem value="email">דואר אלקטרוני</MenuItem>
                </Select>
              </FormControl>
            </div>

            {fields.send_type === "address" && (
              <div className="col-12 mb-3">
                <TextField
                  label="כתובת למשלוח *"
                  fullWidth
                  value={fields.delivery_address || ""}
                  onChange={(e) =>
                    setFields((s) => ({
                      ...s,
                      delivery_address: e.target.value,
                    }))
                  }
                  error={!!errors.delivery_address}
                  helperText={errors.delivery_address}
                />
              </div>
            )}
          </>
        );

      case "invoiceCopyRequest_Pwr":
        return (
          <>
            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth>
                <InputLabel id="invoice-type-power-label">
                  סוג חשבונית
                </InputLabel>
                <Select
                  labelId="invoice-type-power-label"
                  value={fields.invoice_type || ""}
                  label="סוג חשבונית *"
                  onChange={(e) =>
                    setFields((s) => ({ ...s, invoice_type: e.target.value }))
                  }
                  sx={{ direction: "rtl", textAlign: "right" }}
                  inputProps={{ style: { textAlign: "right" } }}
                  error={!!errors.invoice_type}
                >
                  <MenuItem
                    value="last_invoice"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    חשבונית אחרונה
                  </MenuItem>
                  <MenuItem
                    value="two_last_invoice"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    שתי חשבוניות אחרונות
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="col-12">
              <Typography variant="body1" sx={{ mt: -1.5, mb: 2 }}>
                המסמך יישלח לדואר האלקטרוני שהוזן.
              </Typography>
            </div>
          </>
        );

      case CHANGE_PLAN_SERVICE_VALUE:
        return (
          <>
            {/* first div (existing) */}
            <div className="col-12 mb-3">
              <FormControl fullWidth error={!!errors.new_plan}>
                <FormLabel sx={{ mb: 1, textAlign: "left", fontSize: 18 }}>
                  נא לבחור מסלול חדש: *
                </FormLabel>
                <PlanCardsSelect
                  items={visibleChangePlanItems}
                  value={fields.new_plan || ""}
                  error={!!errors.new_plan}
                  onChange={(selectedValue) =>
                    setFields((s) => ({ ...s, new_plan: selectedValue }))
                  }
                  onCtaClick={() => {
                    setFields((s) => ({
                      ...s,
                      change_plan_cta_clicked_at: new Date().toISOString(),
                    }));
                  }}
                />
              </FormControl>
            </div>

            {/* ✅ NEW div AFTER the first div: checkbox + validation */}
            <div className="col-12 mb-3">
              <FormControl
                fullWidth
                error={!!errors.change_plan_terms_approved}
              >
                <FormControlLabel
                  sx={{
                    alignSelf: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "1.15rem", // ✅ bigger label text
                      fontWeight: 600, // optional
                    },
                  }}
                  control={
                    <Checkbox
                      checked={!!fields.change_plan_terms_approved}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFields((s) => ({
                          ...s,
                          change_plan_terms_approved: checked,
                        }));
                      }}
                    />
                  }
                  label="קראתי ואישרתי את תנאי המסלול"
                />
                {!!errors.change_plan_terms_approved && (
                  <div
                    style={{
                      color: "#d32f2f",
                      marginTop: 4,
                      fontSize: "1.15rem",
                    }}
                  >
                    {errors.change_plan_terms_approved}
                  </div>
                )}
              </FormControl>
            </div>
          </>
        );

      case "updateEmail_Pwr":
        return (
          <div className="col-12 mb-3">
            <TextField
              label="כתובת דואר אלקטרוני חדשה *"
              fullWidth
              type="email"
              value={fields.new_email || ""}
              onChange={(e) =>
                setFields((s) => ({ ...s, new_email: e.target.value }))
              }
              error={!!errors.new_email}
              helperText={errors.new_email}
            />
          </div>
        );

      case "terminationOfService_Lpg":
      case "ownershipTransfer_Lpg":
        return (
          <>
            {/* Common LPG details */}
            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="מספר צרכן"
                fullWidth
                inputProps={{
                  dir: "ltr",
                  style: { textAlign: "right" },
                  inputMode: "numeric",
                  pattern: "\\d*",
                  maxLength: 11,
                  minLength: 5,
                }}
                value={fields.lpg_consumer_number || ""}
                onChange={(e) =>
                  setFields((s) => ({
                    ...s,
                    lpg_consumer_number: toDigits(e.target.value),
                  }))
                }
                error={!!errors.lpg_consumer_number}
                helperText={errors.lpg_consumer_number}
              />
            </div>

            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="מספר מונה *"
                fullWidth
                inputProps={{
                  dir: "ltr",
                  style: { textAlign: "right" },
                  inputMode: "text",
                  pattern: "\\d+",
                  maxLength: 17,
                  minLength: 4,
                }}
                value={fields.lpg_meter_number || ""}
                onChange={(e) =>
                  setFields((s) => ({
                    ...s,
                    lpg_meter_number: toDigits(e.target.value),
                  }))
                }
                error={!!errors.lpg_meter_number}
                helperText={errors.lpg_meter_number}
              />
            </div>

            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="קריאת מונה עדכנית *"
                fullWidth
                inputProps={{
                  dir: "ltr",
                  style: { textAlign: "right" },
                  inputMode: "text",
                  pattern: "\\d+",
                  maxLength: 25,
                  minLength: 1,
                }}
                value={fields.lpg_meter_reading || ""}
                onChange={(e) =>
                  setFields((s) => ({
                    ...s,
                    lpg_meter_reading: toDigits(e.target.value),
                  }))
                }
                error={!!errors.lpg_meter_reading}
                helperText={errors.lpg_meter_reading}
              />
            </div>

            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth error={!!errors.lpg_meter_photo}>
                <FormLabel sx={{ mb: 1 }}>העלאת צילום מונה *</FormLabel>
                <Button variant="outlined" component="label">
                  בחר קובץ תמונה
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setFields((s) => ({
                        ...s,
                        lpg_meter_photo: file || null,
                      }));
                    }}
                  />
                </Button>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {fields.lpg_meter_photo
                    ? fields.lpg_meter_photo.name
                    : "לא נבחר קובץ"}
                </Typography>
                <div style={{ color: "#d32f2f" }}>{errors.lpg_meter_photo}</div>
              </FormControl>
            </div>

            {/* הערות */}
            <div className="col-12 mb-3">
              <TextField
                label="הערות"
                fullWidth
                multiline
                minRows={2}
                value={fields.message || ""}
                onChange={(e) =>
                  setFields((s) => ({ ...s, message: e.target.value }))
                }
                error={!!errors.message}
                helperText={errors.message}
              />
            </div>

            {/* Incoming (ownership transfer only) */}
            {selectedService?.value === "ownershipTransfer_Lpg" && (
              <>
                <div className="col-12">
                  <FormLabel
                    sx={{
                      mt: 2,
                      mb: 1,
                      pr: 0.5,
                      display: "block",
                      fontSize: 22,
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    פרטי הלקוח הנכנס:
                  </FormLabel>
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <TextField
                    label="שם פרטי *"
                    fullWidth
                    inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
                    value={fields.incoming_first_name || ""}
                    onChange={(e) =>
                      setFields((s) => ({
                        ...s,
                        incoming_first_name: e.target.value,
                      }))
                    }
                    error={!!errors.incoming_first_name}
                    helperText={errors.incoming_first_name}
                  />
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <TextField
                    label="שם משפחה *"
                    fullWidth
                    inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
                    value={fields.incoming_last_name || ""}
                    onChange={(e) =>
                      setFields((s) => ({
                        ...s,
                        incoming_last_name: e.target.value,
                      }))
                    }
                    error={!!errors.incoming_last_name}
                    helperText={errors.incoming_last_name}
                  />
                </div>

                {/* 🔹 NEW: incoming phone with prefix + number */}
                <div className="col-12 col-md-6 mb-3">
                  <div className="row g-2">
                    <div className="col-8">
                      <TextField
                        label="טלפון *"
                        fullWidth
                        inputProps={{
                          dir: "ltr",
                          style: { textAlign: "right" },
                          maxLength: 7,
                          inputMode: "numeric",
                          pattern: "\\d+",
                        }}
                        value={fields.incoming_phone || ""}
                        onChange={(e) =>
                          setFields((s) => ({
                            ...s,
                            incoming_phone: toDigits(e.target.value),
                          }))
                        }
                        error={!!errors.incoming_phone}
                        helperText={errors.incoming_phone}
                        type="tel"
                      />
                    </div>
                    <div className="col-4">
                      <TextField
                        select
                        label="קידומת *"
                        fullWidth
                        value={fields.incoming_phone_prefix || ""}
                        onChange={(e) =>
                          setFields((s) => ({
                            ...s,
                            incoming_phone_prefix: e.target.value,
                          }))
                        }
                        error={!!errors.incoming_phone_prefix}
                        helperText={errors.incoming_phone_prefix}
                      >
                        {allowedPrefixes.map((prefix) => (
                          <MenuItem key={prefix} value={prefix}>
                            {prefix}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <TextField
                    label="כתובת מייל"
                    fullWidth
                    inputProps={{ dir: "ltr", style: { textAlign: "right" } }}
                    value={fields.incoming_email || ""}
                    onChange={(e) =>
                      setFields((s) => ({
                        ...s,
                        incoming_email: e.target.value,
                      }))
                    }
                    error={!!errors.incoming_email}
                    helperText={errors.incoming_email}
                  />
                </div>
              </>
            )}
          </>
        );

      default:
        return null;
    }
  }

  const validate = async () => {
    try {
      await validationSchema.validate(fields, {
        abortEarly: false,
        context: { service: selectedService?.value },
      });
      setErrors({});
      return { valid: true, messages: [] };
    } catch (err: any) {
      const newErrors: typeof errors = {};
      const messages: string[] = [];
      if (err.inner) {
        err.inner.forEach((validationErr: yup.ValidationError) => {
          if (validationErr.path) {
            newErrors[validationErr.path as keyof State] =
              validationErr.message;
            messages.push(validationErr.message);
          }
        });
      }
      setErrors(newErrors);
      return { valid: false, messages };
    }
  };

  function buildFormData(payload: Record<string, any>) {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        if (k === "lpg_meter_photo" && v instanceof File) {
          fd.append(k, v, v.name);
        } else {
          fd.append(k, String(v));
        }
      }
    });
    return fd;
  }

  const trackSpecSubmit = (serviceValue?: string) => {
    if (serviceValue === CHANGE_PLAN_SERVICE_VALUE) {
      // Analytics: request_for_change_route_submit fires when a validated Change Route form is submitted.
      trackEvent("request_for_change_route_submit", {
        package_type: getChangePlanTitle(fields.new_plan || ""),
      });
    }

    if (serviceValue && REQUEST_DETAILS_SERVICE_VALUES.has(serviceValue)) {
      // Analytics: request_for_details_submit fires when a validated Request Details form is submitted.
      trackEvent("request_for_details_submit");
    }
  };

  const handleSubmit = async (e: React.FormEvent, forceDuplicate = false) => {
    e.preventDefault?.();
    pushGTMEvent("form_submit_attempt", selectedService, {
      is_duplicate: false,
    });

    const { valid } = await validate();
    if (!valid) return;

    trackSpecSubmit(selectedService?.value);
    setLoading(true);

    const serviceVal = selectedService?.value;
    const normalizedFields =
      serviceVal === "invoiceCopyRequest_Pwr"
        ? { ...fields, send_type: "email", delivery_address: "" }
        : fields;

    const payloadFields =
      serviceVal === CHANGE_PLAN_SERVICE_VALUE
        ? normalizedFields
        : stripChangePlanFields(normalizedFields);

    // 🔹 Combine incoming phone for ownership transfer
    const combinedIncomingPhone =
      serviceVal === "ownershipTransfer_Lpg"
        ? `${fields.incoming_phone_prefix || ""}${fields.incoming_phone || ""}`
        : fields.incoming_phone;

    const mergedMessage = serviceVal
      ? getAdditionalMessage(serviceVal, normalizedFields)
      : "";

    const basePayload = {
      ...payloadFields,
      cust_phone: `${fields.cust_phone_prefix}${fields.cust_phone}`,
      ...(serviceVal === "ownershipTransfer_Lpg" && {
        incoming_phone: combinedIncomingPhone, // 🔹 send combined
      }),
      message: mergedMessage,
      forceDuplicate,
    };

    const needsMultipart =
      serviceVal === "terminationOfService_Lpg" ||
      serviceVal === "ownershipTransfer_Lpg";

    const finalPayload = needsMultipart
      ? buildFormData(basePayload)
      : basePayload;

    try {
      const apiResult = await submitFormData(finalPayload, selectedService);

      if (apiResult === "success") {
        if (selectedService?.value === CHANGE_PLAN_SERVICE_VALUE) {
          pushGTMEvent("form_submit_success", selectedService, {
            is_duplicate: false,
          });
          setFields(initialState);
          setErrors({});
          onSuccess?.();
          navigate(ELECTRICITY_PLAN_THANK_YOU_PATH, { replace: true });
        } else if (selectedService) {
          const msg = getSuccessByService(selectedService.value);
          showModal({
            type: "success",
            title: msg.title,
            subtitle: msg.subtitle,
            specialInfo:
              selectedService.value === "tecCheckSchedule_Lpg"
                ? "יש לוודא שבזמן ביקור הטכנאי יהיה נוכח אדם מעל גיל 18 בבית, אין להשתמש בגז לפחות חצי שעה לפני הגעת הטכנאי "
                : undefined,
          });
          pushGTMEvent("form_submit_success", selectedService, {
            is_duplicate: false,
          });
          onSuccess?.();
        } else {
          showModal({ type: "success", title: "הטופס נשלח בהצלחה!" });
          onSuccess?.();
        }
        setFields(initialState);
        setErrors({});
      } else {
        showModal({
          type: "error",
          title: "שגיאה בשליחת הטופס!",
          subtitle: "נסה שוב מאוחר יותר.",
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setPendingPayload(finalPayload);
        setDuplicateDialog(true);
        pushGTMEvent("form_duplicate_detected", selectedService);
      } else {
        showModal({
          type: "error",
          title: "שגיאה בשליחת הטופס!",
          subtitle: "נסה שוב מאוחר יותר.",
        });
        pushGTMEvent("form_submit_error", selectedService, {
          error_message: err?.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceSubmit = async () => {
    setDuplicateDialog(false);
    setLoading(true);
    try {
      await submitFormData(
        pendingPayload instanceof FormData
          ? (() => {
              pendingPayload.set("forceDuplicate", "true");
              return pendingPayload;
            })()
          : { ...pendingPayload, forceDuplicate: true },
        selectedService,
      );

      pushGTMEvent("duplicate_form_submit_success", selectedService, {
        is_duplicate: true,
      });

      if (selectedService?.value === CHANGE_PLAN_SERVICE_VALUE) {
        setFields(initialState);
        setErrors({});
        setPendingPayload(null);
        onSuccess?.();
        navigate(ELECTRICITY_PLAN_THANK_YOU_PATH, { replace: true });
        return;
      }

      if (selectedService) {
        const msg = getSuccessByService(selectedService.value);
        showModal({
          type: "success",
          title: msg.title,
          subtitle: msg.subtitle,
          specialInfo:
            selectedService.value === "tecCheckSchedule_Lpg"
              ? "יש לוודא שבזמן ביקור הטכנאי יהיה נוכח אדם מעל גיל 18 בבית "
              : undefined,
        });
      } else {
        showModal({ type: "success", title: "הטופס נשלח בהצלחה!" });
      }

      setFields(initialState);
      setErrors({});
      setPendingPayload(null);
      onSuccess?.();
    } catch (err: any) {
      showModal({
        type: "error",
        title: "שגיאה בשליחת הטופס!",
        subtitle: "נסה שוב מאוחר יותר.",
      });
      pushGTMEvent("duplicate_form_submit_error", selectedService, {
        is_duplicate: true,
        error_message: err?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateCancel = () => {
    setDuplicateDialog(false);
    setPendingPayload(null);
    pushGTMEvent("form_duplicate_cancel", selectedService);
    window.location.replace("/");
  };

  // ----- Render -----
  return (
    <>
      <Paper
        elevation={3}
        sx={{
          mt: 0,
          p: 3,
          borderRadius: 1,
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          mx: "auto",
        }}
      >
        <div className="col-12" style={{ marginBottom: "10px" }}>
          {selectedService?.value === "terminationOfService_Lpg" ? (
            <h6 style={{ textAlign: "center", fontWeight: 800 }}>
              תודה על התקופה המשותפת 🙂
              <br />
              נשמח ללוות אתכם גם בשלב סיום ההתקשרות – בקלות ובפשטות.
              <br />
              כדי שנוכל לטפל בבקשה בצורה מדויקת, נבקש למלא את הפרטים בסמוך ליום
              העזיבה בפועל, כך שקריאת המונה תבוצע על הנתונים העדכניים ביותר.
            </h6>
          ) : null}

          {selectedService?.value === "ownershipTransfer_Lpg" ? (
            <h6 style={{ textAlign: "center" }}>
              אם הגעתם לכאן, זה אומר שאתם עוברים דירה – אז מברוק ובהצלחה 🙂
              <br />
              נשמח ללוות אתכם בשלב העברת בעלות לחשבון הגז – בקלות ובפשטות.
              <br />
              שימו 💚 נבקש למלא את הבקשה בסמוך ליום המעבר בפועל, כדי שקריאת
              המונה תהיה מדויקת.
            </h6>
          ) : null}
        </div>

        {selectedService && selectedService.type === "function" && (
          <Typography
            variant="h6"
            fontWeight={700}
            gutterBottom
            color="primary"
            sx={{ textAlign: "center", marginBottom: "10px" }}
          >
            {getTitleByServiceValue(selectedService.value)}
          </Typography>
        )}

        <form
          onSubmit={(e) => handleSubmit(e, false)}
          autoComplete="off"
          style={{ width: "100%" }}
        >
          {selectedService?.value === "ownershipTransfer_Lpg" && (
            <div className="row">
              <div className="col-12">
                <FormLabel
                  sx={{
                    mt: 2,
                    mb: 1,
                    pr: 0.5,
                    display: "block",
                    fontSize: 22,
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  פרטי הלקוח היוצא:
                </FormLabel>
              </div>
            </div>
          )}

          <div className="row">
            {/* base fields */}
            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="שם פרטי *"
                fullWidth
                inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
                value={fields.cust_first_name}
                onChange={(e) =>
                  setFields((s) => ({ ...s, cust_first_name: e.target.value }))
                }
                error={!!errors.cust_first_name}
                helperText={errors.cust_first_name}
              />
            </div>

            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="שם משפחה *"
                fullWidth
                inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
                value={fields.cust_last_name}
                onChange={(e) =>
                  setFields((s) => ({ ...s, cust_last_name: e.target.value }))
                }
                error={!!errors.cust_last_name}
                helperText={errors.cust_last_name}
              />
            </div>

            {/* 🔹 Address with City & Street Autocomplete (dependent) + house & apt */}
            <div className="col-12 col-md-6 mb-3">
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, pr: 0.5 }}>כתובת מגורים</FormLabel>
                <div className="row g-2">
                  {/* City */}
                  <div className="col-12 col-lg-3">
                    <Autocomplete
                      options={cities.map((c: any) => c["שם_ישוב"])}
                      value={fields.cust_city || null}
                      onChange={(_, newVal) => {
                        setFields((s) => ({
                          ...s,
                          cust_city: newVal ?? "",
                          cust_address: "", // clear street
                        }));
                      }}
                      onInputChange={(_, newInput) => {
                        setFields((s) => ({ ...s, cust_city: newInput }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="עיר *"
                          inputProps={{
                            ...params.inputProps,
                            dir: "rtl",
                            style: { textAlign: "right" },
                          }}
                          error={!!errors.cust_city}
                          helperText={errors.cust_city}
                        />
                      )}
                    />
                  </div>

                  {/* Street (depends on selected city) */}
                  <div className="col-12 col-lg-5">
                    <Autocomplete
                      options={streetOptions}
                      value={fields.cust_address || null}
                      onChange={(_, newVal) =>
                        setFields((s) => ({ ...s, cust_address: newVal ?? "" }))
                      }
                      onInputChange={(_, newInput) =>
                        setFields((s) => ({ ...s, cust_address: newInput }))
                      }
                      disabled={!selectedCity}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="רחוב *"
                          inputProps={{
                            ...params.inputProps,
                            dir: "rtl",
                            style: { textAlign: "right" },
                          }}
                          error={!!errors.cust_address}
                          helperText={
                            !selectedCity
                              ? "יש לבחור עיר תחילה"
                              : errors.cust_address
                          }
                        />
                      )}
                    />
                  </div>

                  {/* House */}
                  <div className="col-6 col-lg-2">
                    <TextField
                      label="בית *"
                      fullWidth
                      inputProps={{
                        dir: "rtl",
                        style: { textAlign: "right" },
                        maxLength: 6,
                      }}
                      value={fields.cust_house_num}
                      onChange={(e) =>
                        setFields((s) => ({
                          ...s,
                          cust_house_num: e.target.value,
                        }))
                      }
                      error={!!errors.cust_house_num}
                      helperText={errors.cust_house_num}
                    />
                  </div>

                  {/* Apt */}
                  <div className="col-6 col-lg-2">
                    <TextField
                      label="דירה *"
                      fullWidth
                      inputProps={{
                        dir: "rtl",
                        style: { textAlign: "right" },
                        maxLength: 6,
                      }}
                      value={fields.cust_apt_num}
                      onChange={(e) =>
                        setFields((s) => ({
                          ...s,
                          cust_apt_num: e.target.value,
                        }))
                      }
                      error={!!errors.cust_apt_num}
                      helperText={errors.cust_apt_num}
                    />
                  </div>
                </div>
              </FormControl>
            </div>

            {/* Email / Phone */}
            <div className="col-12 col-md-6 mb-3">
              <TextField
                label="כתובת דואר אלקטרוני"
                fullWidth
                inputProps={{ dir: "ltr", style: { textAlign: "right" } }}
                value={fields.cust_email}
                onChange={(e) =>
                  setFields((s) => ({ ...s, cust_email: e.target.value }))
                }
                error={!!errors.cust_email}
                helperText={errors.cust_email}
              />
            </div>

            <div className="col-12 col-md-6 mb-3 ">
              <div className="row g-2">
                <div className="col-8">
                  <TextField
                    label="טלפון *"
                    fullWidth
                    inputProps={{
                      dir: "ltr",
                      style: { textAlign: "right" },
                      maxLength: 7,
                    }}
                    value={fields.cust_phone}
                    onChange={(e) =>
                      setFields((s) => ({ ...s, cust_phone: e.target.value }))
                    }
                    error={!!errors.cust_phone}
                    helperText={errors.cust_phone}
                    type="tel"
                  />
                </div>
                <div className="col-4">
                  <TextField
                    select
                    label="קידומת *"
                    fullWidth
                    value={fields.cust_phone_prefix}
                    onChange={(e) =>
                      setFields((s) => ({
                        ...s,
                        cust_phone_prefix: e.target.value,
                      }))
                    }
                    error={!!errors.cust_phone_prefix}
                    helperText={errors.cust_phone_prefix}
                  >
                    {allowedPrefixes.map((prefix) => (
                      <MenuItem key={prefix} value={prefix}>
                        {prefix}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
            </div>

            <Divider orientation="vertical" flexItem />

            {renderDynamicFields()}

            <div className="col-12 text-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                sx={{
                  minWidth: 120,
                  fontWeight: 700,
                  mt: 1,
                  fontSize: "1.2rem",
                }}
                disabled={loading}
              >
                {selectedService?.value === CHANGE_PLAN_SERVICE_VALUE
                  ? "מעבר מסלול"
                  : "שלח"}
              </Button>
              {loading && (
                <CircularProgress
                  size={26}
                  sx={{ ml: 2, verticalAlign: "middle" }}
                />
              )}
            </div>
          </div>
        </form>
      </Paper>

      {/* Duplicate request dialog */}
      <Dialog open={duplicateDialog} onClose={handleDuplicateCancel}>
        <DialogTitle sx={{ textAlign: "center" }}>קיימת בקשה פתוחה</DialogTitle>
        <DialogContent sx={{ minWidth: 300, textAlign: "center" }} />
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={handleForceSubmit}
            variant="contained"
            color="primary"
            sx={{ minWidth: 120, fontWeight: 700, ml: 2 }}
          >
            בקשה חדשה
          </Button>
          <Button
            onClick={handleDuplicateCancel}
            variant="outlined"
            color="secondary"
            sx={{ minWidth: 100, fontWeight: 700 }}
          >
            ביטול
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
